import React, { useState, useCallback, useMemo, useId, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Chip from "./Chip";
import DropdownPanel from "./DropdownPanel";
import { useMultiSelect } from "./useMultiSelect";
import styles from "./MultiSelectDropdown.module.css";

/**
 * @typedef {{ value: string; label: string; icon?: React.ReactNode; disabled?: boolean; group?: string }} Option
 */

/**
 * @param {{
 *   options: Option[];
 *   value: string[];
 *   onChange: (values: string[]) => void;
 *   placeholder?: string;
 *   maxSelected?: number;
 *   searchable?: boolean;
 *   disabled?: boolean;
 *   className?: string;
 *   style?: React.CSSProperties;
 *   "aria-label"?: string;
 *   optionColumns?: 1 | 2;
 *   popout?: boolean;
 * }} props
 */
const MultiSelectDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select…",
  maxSelected,
  searchable = false,
  disabled = false,
  className = "",
  style,
  optionColumns = 1,
  popout = false,
  "aria-label": ariaLabel = "Multi-select",
}) => {
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const controlRef = useRef(null);
  const dropdownPanelRef = useRef(null);
  const [popoutStyle, setPopoutStyle] = useState(null);
  const {
    searchQuery,
    setSearchQuery,
    highlightedIndex,
    setHighlightedIndex,
    filteredOptions,
    containerRef,
    searchInputRef,
    atMax,
    toggleValue,
    selectHighlighted,
  } = useMultiSelect({
    options,
    value,
    searchable,
    maxSelected,
    isOpen,
    setIsOpen,
    dropdownRef: popout ? dropdownPanelRef : undefined,
  });

  const updatePopoutPosition = useCallback(() => {
    if (!controlRef.current) return;
    const rect = controlRef.current.getBoundingClientRect();
    const minWidth = optionColumns === 2 ? 380 : rect.width;
    const width = Math.min(
      Math.max(rect.width, minWidth),
      window.innerWidth - 24
    );
    const left = Math.min(
      Math.max(12, rect.left),
      window.innerWidth - width - 12
    );
    const dropdownHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < dropdownHeight && rect.top > spaceBelow;

    setPopoutStyle({
      position: "fixed",
      left,
      width,
      top: openAbove ? undefined : rect.bottom + 4,
      bottom: openAbove ? window.innerHeight - rect.top + 4 : undefined,
      zIndex: 10000,
    });
  }, [optionColumns]);

  useEffect(() => {
    if (!popout || !isOpen) {
      setPopoutStyle(null);
      return undefined;
    }
    updatePopoutPosition();
    const onReposition = () => updatePopoutPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [popout, isOpen, updatePopoutPosition]);

  const labelByValue = useMemo(() => {
    const m = new Map();
    options.forEach((o) => m.set(o.value, o.label));
    return m;
  }, [options]);

  const removeChip = useCallback(
    (val) => {
      onChange(value.filter((v) => v !== val));
    },
    [value, onChange]
  );

  const clearAll = useCallback(
    (e) => {
      e.stopPropagation();
      onChange([]);
    },
    [onChange]
  );

  const openDropdown = useCallback(() => {
    if (!disabled) setIsOpen(true);
  }, [disabled]);

  const handleControlClick = useCallback(() => {
    if (disabled) return;
    setIsOpen((o) => !o);
  }, [disabled]);

  const handleSelectOption = useCallback(
    (val) => {
      const next = toggleValue(val);
      if (next && next !== value) onChange(next);
    },
    [toggleValue, value, onChange]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;

      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        return;
      }

      if (e.key === "Backspace") {
        const searchFocused =
          searchable &&
          isOpen &&
          document.activeElement === searchInputRef.current;
        if (searchFocused && searchQuery.length > 0) return;
        if (value.length > 0) {
          e.preventDefault();
          onChange(value.slice(0, -1));
        }
        return;
      }

      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
          e.preventDefault();
          openDropdown();
        }
        return;
      }

      const len = filteredOptions.length;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, Math.max(0, len - 1)));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const next = selectHighlighted();
        if (next) onChange(next);
        return;
      }
    },
    [
      disabled,
      isOpen,
      searchable,
      searchQuery,
      searchInputRef,
      value,
      onChange,
      filteredOptions.length,
      setHighlightedIndex,
      openDropdown,
      selectHighlighted,
    ]
  );

  const showClear = value.length > 0 && !disabled;

  const dropdownPanel = isOpen && !disabled && (
    <DropdownPanel
      id={listboxId}
      searchable={searchable}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchInputRef={searchInputRef}
      filteredOptions={filteredOptions}
      totalOptionCount={options.length}
      value={value}
      highlightedIndex={highlightedIndex}
      onHighlight={setHighlightedIndex}
      onSelectOption={handleSelectOption}
      maxSelected={maxSelected}
      atMax={atMax}
      optionColumns={optionColumns}
      panelRef={popout ? dropdownPanelRef : undefined}
      className={popout ? styles.dropdownPopout : ""}
      style={popout ? popoutStyle ?? undefined : undefined}
    />
  );

  return (
    <div
      ref={containerRef}
      className={[styles.root, className].filter(Boolean).join(" ")}
      style={style}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={controlRef}
        className={[
          styles.control,
          isOpen ? styles.controlOpen : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleControlClick}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-disabled={disabled}
      >
        <div className={styles.chipsWrap}>
          {value.map((v) => {
            const opt = options.find((o) => o.value === v);
            return (
              <Chip
                key={v}
                label={opt?.label ?? labelByValue.get(v) ?? v}
                icon={opt?.icon}
                onRemove={() => removeChip(v)}
                disabled={disabled}
                focused={false}
              />
            );
          })}
          {value.length === 0 && (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        {showClear && (
          <button
            type="button"
            className={styles.clearAll}
            onClick={clearAll}
            aria-label="Clear all"
            title="Clear all"
          >
            ×
          </button>
        )}
        <span
          className={[styles.chevron, isOpen ? styles.chevronOpen : ""].filter(Boolean).join(" ")}
          aria-hidden
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </div>

      {popout
        ? isOpen && !disabled && popoutStyle && createPortal(dropdownPanel, document.body)
        : dropdownPanel}
      {!isOpen && <div id={listboxId} hidden aria-hidden="true" />}
    </div>
  );
};

export default MultiSelectDropdown;
export { Chip, DropdownPanel, useMultiSelect };
